import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw, Sparkles } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import api from '../utils/api';

/**
 * AppUpdateManager — live-session auto-update detection (2026-06-20).
 *
 * The existing boot-freshness check (runtimeFreshness.ensureFreshRuntimeAtBoot)
 * only runs once at app boot, and the SW update toast depends on a service
 * worker (unreliable on mobile browsers / dev). Users therefore had to manually
 * refresh to pick up a new deploy. This component closes that gap:
 *
 *   • Snapshots the backend build_id (/api/version) on mount.
 *   • Re-polls on an interval AND whenever the tab regains focus/visibility —
 *     the most common moment a PWA user returns after a deploy.
 *   • On a build_id change it shows a branded "What's New at iEvntür!" prompt
 *     with an "Update Now" button that reloads into the latest version.
 *   • Auto-reloads WHEN SAFE (user idle, not on a sensitive route, and no
 *     in-flight transaction) so most users never even have to tap a button.
 *   • Never interrupts an active booking, payment, form submission or other
 *     unfinished transaction.
 *   • Dismissal is per-build so the same update never nags twice, but a NEW
 *     deploy always re-prompts.
 *
 * Works on mobile browsers, desktop browsers and installed PWAs because it
 * relies only on a plain fetch of /api/version — no service worker required.
 */

const POLL_MS = 90 * 1000;          // background poll cadence
const AUTO_RELOAD_GRACE_MS = 45000; // wait this long before a safe auto-reload
const ACTIVITY_IDLE_MS = 30000;     // "idle" = no interaction for this long
const DISMISS_KEY = 'ievntur_update_dismissed_build';

// Routes where an auto-reload could lose user work — never reload behind
// their back here (they can still tap Update Now).
const SENSITIVE_PATH_PATTERNS = [
  /\/talent\/(shifts|gigs)\/[^/]+/,
  /\/one-tap-apply/,
  /\/checkout/,
  /\/payment/,
  /\/pay\//,
  /\/checkin/,
  /\/clock-?in/i,
  /\/clock-?out/i,
  /\/survey/,
  /\/create/i,
  /\/book/i,
  /\/profile\/(idv|verify|verification)/i,
];

const isOnSensitiveRoute = () =>
  SENSITIVE_PATH_PATTERNS.some((re) => re.test(window.location.pathname || ''));

// A global flag any flow can set while a transaction is in-flight:
//   window.__ievntur_tx_active = true;  // ...and reset to false when done
const isTransactionActive = () => {
  try {
    if (window.__ievntur_tx_active) return true;
    const el = document.activeElement;
    if (el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName || '')) return true;
    // An open modal/dialog usually means the user is mid-task.
    if (document.querySelector('[role="dialog"][data-state="open"]')) return true;
  } catch (_) { /* noop */ }
  return false;
};

const fetchBuildId = async () => {
  try {
    const { data } = await api.get('/version', {
      params: { _t: Date.now() },
      timeout: 6000,
    });
    return data && typeof data.build_id === 'string' ? data.build_id : null;
  } catch (_) {
    return null;
  }
};

const AppUpdateManager = () => {
  const [show, setShow] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const baselineRef = useRef(null);
  const pendingRef = useRef(null);
  const newBuildRef = useRef(null);
  const lastInteractRef = useRef(Date.now());

  const doReload = useCallback(() => {
    setRefreshing(true);
    if (newBuildRef.current) {
      try { localStorage.removeItem(DISMISS_KEY); } catch (_) { /* noop */ }
    }
    setTimeout(() => { try { window.location.reload(); } catch (_) { /* noop */ } }, 200);
  }, []);

  const flagUpdate = useCallback((buildId) => {
    newBuildRef.current = buildId;
    let dismissed = null;
    try { dismissed = localStorage.getItem(DISMISS_KEY); } catch (_) { /* noop */ }
    // Even if dismissed, we keep the (silent) option to auto-reload when safe,
    // but we don't re-open the modal for a build the user already waved off.
    if (dismissed !== buildId) setShow(true);
  }, []);

  // Track interaction so we can tell when the user is idle.
  useEffect(() => {
    const bump = () => { lastInteractRef.current = Date.now(); };
    window.addEventListener('mousedown', bump, { passive: true });
    window.addEventListener('keydown', bump, { passive: true });
    window.addEventListener('touchstart', bump, { passive: true });
    return () => {
      window.removeEventListener('mousedown', bump);
      window.removeEventListener('keydown', bump);
      window.removeEventListener('touchstart', bump);
    };
  }, []);

  // Snapshot baseline + wire polling and focus/visibility re-checks.
  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const check = async () => {
      const current = await fetchBuildId();
      if (cancelled || !current) return;
      if (!baselineRef.current) {
        baselineRef.current = current;
        try { window.__IEVNTUR_LOADED_BUILD_ID__ = current; } catch (_) { /* noop */ }
        return;
      }
      if (current === baselineRef.current) {
        // Back to the known-good build — clear any pending candidate so a
        // transient blip (e.g. a load-balanced worker answering with a
        // different fingerprint for one request) never prompts.
        pendingRef.current = null;
        return;
      }
      // A different build_id appeared. Require it to be seen on TWO
      // consecutive checks before prompting. Round-robin flapping across
      // workers/replicas alternates values, so it can never confirm — only a
      // real, stable new deploy will.
      if (pendingRef.current === current) {
        baselineRef.current = current; // adopt so we stop re-comparing to stale
        pendingRef.current = null;
        flagUpdate(current);
      } else {
        pendingRef.current = current;
      }
    };

    check();
    timer = setInterval(check, POLL_MS);

    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', onVisible);

    // Test / manual hook — lets QA trigger the prompt without a real deploy.
    try {
      window.__ievnturForceUpdatePrompt = () => flagUpdate('qa-forced-build');
    } catch (_) { /* noop */ }

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [flagUpdate]);

  // Safe auto-reload: once an update is pending, reload silently when the user
  // is idle, not on a sensitive route, and has no transaction in flight.
  useEffect(() => {
    if (!show) return undefined;
    const t = setTimeout(() => {
      const idle = Date.now() - lastInteractRef.current > ACTIVITY_IDLE_MS;
      if (idle && !isOnSensitiveRoute() && !isTransactionActive()) {
        doReload();
      }
    }, AUTO_RELOAD_GRACE_MS);
    return () => clearTimeout(t);
  }, [show, doReload]);

  const dismiss = () => {
    setShow(false);
    if (newBuildRef.current) {
      try { localStorage.setItem(DISMISS_KEY, newBuildRef.current); } catch (_) { /* noop */ }
    }
  };

  return (
    <Dialog open={show} onOpenChange={(v) => !v && dismiss()}>
      <DialogContent className="bg-white max-w-md z-[200]" data-testid="app-update-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg text-cyan-700">
            <Sparkles className="w-5 h-5 text-cyan-500" />
            What&apos;s New at iEvntür!
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600">
            We&apos;ve made some improvements to your experience. Your latest features
            and updates are ready!
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 flex-row sm:justify-end">
          <Button
            variant="outline"
            onClick={dismiss}
            disabled={refreshing}
            data-testid="app-update-later-btn"
          >
            Later
          </Button>
          <Button
            onClick={doReload}
            disabled={refreshing}
            className="bg-cyan-600 hover:bg-cyan-700 text-white gap-1.5"
            data-testid="app-update-now-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Updating…' : 'Update Now'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AppUpdateManager;
